import React, { useContext, useState } from "react";
import {
  Card,
  Button,
  Alert,
  Container,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import AuthContext from "../contexts/AuthContext";
import axios from "axios";

const LoginCheck = () => {
  const { auth, logout } = useContext(AuthContext);
  const [apiResponse, setApiResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get("/api/users/me/");
      setApiResponse(response.data);
      console.log("User data:", response.data);
    } catch (err) {
      console.error("Error checking login:", err);
      setError(err.message || "Failed to check login status");
    } finally {
      setLoading(false);
    }
  };

  const decodeJWT = (token) => {
    if (!token) return null;
    try {
      // Get the payload part of the JWT (second part)
      const base64Url = token.split(".")[1];
      // Replace characters and decode
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      // Parse the JSON
      return JSON.parse(window.atob(base64));
    } catch (e) {
      console.error("Error decoding JWT:", e);
      return { error: "Invalid token format" };
    }
  };

  // Extract tokens from localStorage
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");
  const decodedToken = accessToken ? decodeJWT(accessToken) : null;

  return (
    <Container className="my-4">
      <h2>Login Status Check</h2>
      <p>Use this page to check your authentication status and permissions.</p>

      <Card className="mb-4">
        <Card.Header>Authentication Status</Card.Header>
        <Card.Body>
          <p>
            <strong>Is Authenticated:</strong>{" "}
            {auth.isAuthenticated ? "✅ Yes" : "❌ No"}
          </p>
          <p>
            <strong>User:</strong>{" "}
            {auth.user ? auth.user.username : "Not logged in"}
          </p>
          <p>
            <strong>Is Admin:</strong> {auth.isAdmin ? "✅ Yes" : "❌ No"}
          </p>
          <p>
            <strong>Is Staff:</strong>{" "}
            {auth.user?.is_staff ? "✅ Yes" : "❌ No"}
          </p>
          <p>
            <strong>Is Superuser:</strong>{" "}
            {auth.user?.is_superuser ? "✅ Yes" : "❌ No"}
          </p>
          <h5 className="mt-3">Permission Details</h5>
          <p>
            <strong>Can Create:</strong>{" "}
            {auth.user?.profile?.can_create ? "✅ Yes" : "❌ No"}
          </p>
          <p>
            <strong>Can Edit:</strong>{" "}
            {auth.user?.profile?.can_edit ? "✅ Yes" : "❌ No"}
          </p>
          <p>
            <strong>Can Delete:</strong>{" "}
            {auth.user?.profile?.can_delete ? "✅ Yes" : "❌ No"}
          </p>
          <h5 className="mt-3">Token Status</h5>
          <p>
            <strong>Access Token:</strong>{" "}
            {accessToken ? "✅ Present" : "❌ Missing"}
          </p>
          <p>
            <strong>Refresh Token:</strong>{" "}
            {refreshToken ? "✅ Present" : "❌ Missing"}
          </p>
          {decodedToken && (
            <>
              <h5 className="mt-3">Decoded Token</h5>
              <pre>{JSON.stringify(decodedToken, null, 2)}</pre>
              <p>
                <strong>Token Expiry:</strong>{" "}
                {new Date(decodedToken.exp * 1000).toLocaleString()}
              </p>
              <p>
                <strong>User ID in Token:</strong> {decodedToken.user_id}
              </p>
              {auth.user && auth.user.id !== decodedToken.user_id && (
                <Alert variant="warning">
                  Warning: User ID mismatch between auth context and token!
                </Alert>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>API Response from /api/users/me/</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p>Checking login status...</p>
            </div>
          ) : apiResponse ? (
            <>
              <h5>Full API Response:</h5>
              <pre className="border p-3 bg-light">
                {JSON.stringify(apiResponse, null, 2)}
              </pre>
            </>
          ) : (
            <p>Click the button below to fetch your user data from the API.</p>
          )}
          <Button
            variant="primary"
            onClick={checkLogin}
            disabled={loading}
            className="mt-2"
          >
            {loading ? "Checking..." : "Check API Login Status"}
          </Button>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Debug Actions</Card.Header>
        <Card.Body>
          <Button
            variant="outline-secondary"
            onClick={() => {
              axios.defaults.headers.common[
                "Authorization"
              ] = `Bearer ${accessToken}`;
              alert("Authorization header reset with current token");
            }}
            className="me-2"
          >
            Reset Auth Header
          </Button>
          <Button
            variant="outline-danger"
            onClick={() => {
              console.clear();
              alert("Console cleared");
            }}
          >
            Clear Console
          </Button>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default LoginCheck;
